import { getGrpcClient } from '@/services/grpc-client';
import { State } from '@/utils';
import extractErrorMessage from '@/utils/handle-error';
import { CurrentUserService } from 'hiber-grpc';
import { CurrentUser } from 'hiber-grpc/src/customer_compiled/currentuser';
import latinize from 'latinize';
import { defineStore } from 'pinia';
import { useToast } from 'vue-toastification';
import { OrganizationPermission } from 'hiber-grpc/src/customer_compiled/permission';
import { getAuthProvider } from '@/auth/AuthFactory';

interface UserStore {
  isAuthenticated: boolean;
  currentUser: CurrentUser | null;
  useLocalTokenAuth: boolean;
  hasLocalAuthToken: boolean;
  authToken: string;
  state: State;
  dashBoardView: 'processPoints' | 'devices' | null;
}

const toast = useToast();

export const useUserStore = defineStore('user', {
  state: () =>
    ({
      isAuthenticated: false,
      useLocalTokenAuth: import.meta.env.VITE_AUTH_MODE === 'token',
      hasLocalAuthToken: Boolean(localStorage.getItem('local-auth-token')),
      authToken: localStorage.getItem('local-auth-token')?.toString(),
      currentUser: null,
      state: State.READY,
      dashBoardView: null,
    }) as UserStore,
  actions: {
    fetchCurrentUser(forceReload = false): Promise<CurrentUser> {
      if (forceReload || !this.currentUser) {
        return new Promise<CurrentUser>((resolve, reject) => {
          return CurrentUserService.getInstance(getGrpcClient)
            .getCurrentUser()
            .then((result) => {
              this.currentUser = result;
              resolve(result);
            })
            .catch((error) => {
              toast.error(extractErrorMessage(error));
              return reject(error);
            });
        });
      } else {
        return Promise.resolve(this.currentUser);
      }
    },

    setCurrentUser(user: CurrentUser | null) {
      this.currentUser = user;
    },

    async signInWithToken(token: string) {
      this.hasLocalAuthToken = true;
      this.authToken = token;
      return this.fetchCurrentUser(true);
    },

    async signout() {
      if (this.hasLocalAuthToken) {
        localStorage.removeItem('local-auth-token');
        this.hasLocalAuthToken = false;
        this.authToken = '';
      } else if (!this.useLocalTokenAuth) {
        await getAuthProvider().logout();
      }

      this.currentUser = null;
      this.isAuthenticated = false;
    },

    setDashboardView(view: 'processPoints' | 'devices' | null) {
      this.dashBoardView = view;
    },
  },
  getters: {
    user(state) {
      return state.currentUser;
    },
    userHasOrganization(state) {
      return !!state.currentUser?.organizations?.length;
    },
    userInvites(state) {
      return state.currentUser;
    },
    missionControlSettings(state) {
      if (state.currentUser) {
        return JSON.parse(state.currentUser.missionControlSettings);
      }
      return {};
    },
    userInitials(state) {
      let result = '';

      if (!state.currentUser) return result;

      const name = latinize(state.currentUser.name);

      if (name?.length) {
        const initials = name.match(/\b\w/g) || [];
        result = ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
      } else {
        result = state.currentUser?.email?.charAt(0).toUpperCase();
      }
      return result;
    },
    permissions(state) {
      return state.currentUser?.currentOrganizationPermissions;
    },
    hasPermissions(state) {
      return (permissions: OrganizationPermission[] = []) => {
        if (!permissions.length) return true;
        return (
          permissions.every((permission) =>
            state.currentUser?.currentOrganizationPermissions?.include?.includes?.(permission),
          ) || state.currentUser?.currentOrganizationPermissions?.includeAll
        );
      };
    },
  },
});
